import { z } from "zod";

export const API_KEY_NAME_MIN_LENGTH = 1;
export const API_KEY_NAME_MAX_LENGTH = 64;

// Single source of truth for API key name validation. The Better Auth plugin
// options, the tRPC create input, and the dashboard form all derive from this
// so the client can never accept a name the server rejects.
export const apiKeyNameSchema = z
  .string()
  .trim()
  .min(API_KEY_NAME_MIN_LENGTH, "Provide a name for the API key.")
  .max(
    API_KEY_NAME_MAX_LENGTH,
    `API key names are at most ${API_KEY_NAME_MAX_LENGTH} characters.`,
  );

export const apiKeyPermissionSchema = z.enum(["read", "write"]);

export type ApiKeyPermission = z.infer<typeof apiKeyPermissionSchema>;

export const apiKeyProjectScopeSchema = z.object({
  permissions: z.array(apiKeyPermissionSchema),
  projectId: z.string(),
  teamId: z.string().optional(),
});

export type ApiKeyProjectScope = z.infer<typeof apiKeyProjectScopeSchema>;

type ApiKeyScopeRecord = {
  canRead: boolean;
  canWrite: boolean;
  projectId: string;
};

export function toApiKeyPermissions(
  input: Pick<ApiKeyScopeRecord, "canRead" | "canWrite">,
): ApiKeyPermission[] {
  const permissions: ApiKeyPermission[] = [];

  if (input.canRead) {
    permissions.push("read");
  }

  if (input.canWrite) {
    permissions.push("write");
  }

  return permissions;
}

export function toApiKeyProjectScope(
  input: ApiKeyScopeRecord,
): ApiKeyProjectScope {
  return {
    permissions: toApiKeyPermissions(input),
    projectId: input.projectId,
  };
}

export const apiKeyActorSchema = z.object({
  apiKeyId: z.string(),
  createdByUserId: z.string().nullable(),
  defaultProjectId: z.string().nullable(),
  scopes: z.array(apiKeyProjectScopeSchema),
  teamId: z.string(),
});

export type ApiKeyActor = z.infer<typeof apiKeyActorSchema>;

export const apiKeyProjectSelectionSourceSchema = z.enum([
  "default-project",
  "explicit-project",
  "single-scope",
]);

export type ApiKeyProjectSelectionSource = z.infer<
  typeof apiKeyProjectSelectionSourceSchema
>;

export const apiKeyProjectSelectionResultSchema = z.object({
  projectId: z.string(),
  source: apiKeyProjectSelectionSourceSchema,
});

export type ApiKeyProjectSelectionResult = z.infer<
  typeof apiKeyProjectSelectionResultSchema
>;

export type ApiKeyProjectSelectionErrorReason =
  | "project-selection-required"
  | "scope-missing"
  | "permission-denied";

export class ApiKeyProjectSelectionError extends Error {
  constructor(
    public readonly reason: ApiKeyProjectSelectionErrorReason,
    public readonly requiredPermission?: ApiKeyPermission,
  ) {
    super(reason);
    this.name = "ApiKeyProjectSelectionError";
  }
}

function requireApiKeyScope(input: {
  projectId: string;
  requiredPermission?: ApiKeyPermission;
  scopes: ApiKeyProjectScope[];
}) {
  const scope = input.scopes.find(
    (candidateScope) => candidateScope.projectId === input.projectId,
  );

  if (!scope) {
    throw new ApiKeyProjectSelectionError("scope-missing");
  }

  if (
    input.requiredPermission &&
    !scope.permissions.includes(input.requiredPermission)
  ) {
    throw new ApiKeyProjectSelectionError(
      "permission-denied",
      input.requiredPermission,
    );
  }

  return scope;
}

export function selectApiKeyProject(input: {
  defaultProjectId: string | null;
  requiredPermission?: ApiKeyPermission;
  requestedProjectId?: string | null;
  scopes: ApiKeyProjectScope[];
}): ApiKeyProjectSelectionResult {
  if (input.requestedProjectId) {
    return {
      projectId: requireApiKeyScope({
        projectId: input.requestedProjectId,
        requiredPermission: input.requiredPermission,
        scopes: input.scopes,
      }).projectId,
      source: "explicit-project",
    };
  }

  if (input.scopes.length === 1) {
    const [singleScope] = input.scopes;

    if (!singleScope) {
      throw new ApiKeyProjectSelectionError("project-selection-required");
    }

    return {
      projectId: requireApiKeyScope({
        projectId: singleScope.projectId,
        requiredPermission: input.requiredPermission,
        scopes: input.scopes,
      }).projectId,
      source: "single-scope",
    };
  }

  if (input.defaultProjectId) {
    return {
      projectId: requireApiKeyScope({
        projectId: input.defaultProjectId,
        requiredPermission: input.requiredPermission,
        scopes: input.scopes,
      }).projectId,
      source: "default-project",
    };
  }

  throw new ApiKeyProjectSelectionError("project-selection-required");
}

export function isApiKeyCurrentlyActive(input: {
  enabled: boolean;
  expiresAt: Date | null | undefined;
  now?: Date | number;
}) {
  if (!input.enabled) {
    return false;
  }

  if (!input.expiresAt) {
    return true;
  }

  const now =
    input.now instanceof Date ? input.now.getTime() : (input.now ?? Date.now());

  return input.expiresAt.getTime() > now;
}

/**
 * Stored-key hash format: SHA-256 of the UTF-8 plaintext key,
 * base64url-encoded without padding (43 chars). It is the value in
 * `better_auth_api_key.key` and the auth-cache fingerprint, so a partner that
 * hashes a leaked key this way can ask us to revoke it without sending the
 * plaintext. See `hashApiKey` in `@kuzco/db/better-auth-api-key-config`.
 */
const API_KEY_HASH_BASE64URL_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** Lowercase or uppercase hex SHA-256 digest (64 chars), accepted as an alias. */
const API_KEY_HASH_HEX_PATTERN = /^[0-9a-fA-F]{64}$/;

/**
 * Normalizes a reported API-key hash to the stored base64url form. Accepts the
 * stored base64url digest or the same SHA-256 digest as hex (what `sha256sum`
 * prints), so partners can use whichever their tooling emits. Returns null for
 * anything else; a malformed hash can never match a key.
 */
export function normalizeApiKeyHash(input: string): string | null {
  const value = input.trim();
  if (API_KEY_HASH_BASE64URL_PATTERN.test(value)) {
    return value;
  }
  if (!API_KEY_HASH_HEX_PATTERN.test(value)) {
    return null;
  }
  let binary = "";
  for (let index = 0; index < value.length; index += 2) {
    binary += String.fromCharCode(
      Number.parseInt(value.slice(index, index + 2), 16),
    );
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Per-hash outcome of a revoke-by-hash request. Safe to retry. */
export const API_KEY_HASH_REVOCATION_STATUS = {
  REVOKED: "revoked",
  ALREADY_REVOKED: "already_revoked",
  NOT_FOUND: "not_found",
  INVALID_HASH: "invalid_hash",
} as const;

export const apiKeyHashRevocationStatusSchema = z.enum([
  API_KEY_HASH_REVOCATION_STATUS.REVOKED,
  API_KEY_HASH_REVOCATION_STATUS.ALREADY_REVOKED,
  API_KEY_HASH_REVOCATION_STATUS.NOT_FOUND,
  API_KEY_HASH_REVOCATION_STATUS.INVALID_HASH,
]);

export type ApiKeyHashRevocationStatus = z.infer<
  typeof apiKeyHashRevocationStatusSchema
>;

/**
 * Most hashes one revoke-by-hash request may carry. Sized by the relay
 * Worker's per-invocation subrequest budget (~1000, KV ops count): the
 * immediate cache broadcast costs ~3 KV ops per newly revoked key plus a few
 * list pages, so 200 keys (~600 ops) leaves headroom for the DB and audit
 * calls. Larger reports are split client-side. OpenRouter holds ~160 keys.
 */
export const API_KEY_HASH_REVOCATION_MAX_BATCH = 200;
